import { OAUTH_SIGNUP_FLAG_KEY } from '@common/auth/constant';
import { markSsoAttempt } from '@common/auth/ssoAutoLogin/state';
import { openOauthWindow } from '@common/oAuth/utils';
import { message } from '@prophecy/ui-v3/LegacyToast';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { navigateAndRefresh } from '@prophecy/utils/history';
import { appendQueryParams, isSameHost } from '@prophecy/utils/url';
import { useQuery } from '@tanstack/react-query';
import { useUnsecuredGlobalVariables } from 'frontend/core/src/context/globalConfig/unsecuredConfigHook';
import {
  getAllIdpTypes,
  oAuthAzureADAuthenticate,
  oAuthGoogleAuthenticate,
  oAuthSamlAuthenticate
} from 'frontend/core/src/data/apis/api';

import { redirectParentToUrl } from '../utils';

import type { IDPSupportedType } from '../types';
import type { OAuthGoogleAuthenticatePayload } from 'frontend/core/src/data/apis/types';
import type { GlobalWindow } from 'frontend/core/src/utils/types';

export function useExternalLogin(embedded?: boolean, redirectUrl?: string) {
  const unsecureGlobalConfigManager = useUnsecuredGlobalVariables();
  const { data, isLoading } = useQuery(['idpconfigtypes'], getAllIdpTypes, {
    enabled: unsecureGlobalConfigManager.isConfigurableAuthEnabled
  });
  let idpTypesSupported: IDPSupportedType[] = data?.data?.idpTypes || FROZEN_EMPTY_ARRAY;
  const redirectToOauth = (url: string) => {
    markSsoAttempt();
    openOauthWindow(url, () => {
      if (embedded) {
        redirectParentToUrl('/');
      } else {
        // Same-host guard mirrors useRedirectAfterAuth so a crafted ?redirect= can't open-redirect
        if (redirectUrl && isSameHost(redirectUrl, window.location.host)) {
          window.location.assign(redirectUrl);
        } else {
          //Instead of reloading same signin or signup page redirect user to host page directly
          navigateAndRefresh('/');
        }
      }
    });
  };

  async function signInWithAzureAD(params: { identifier?: string } = {}) {
    //linkedin track
    (window as unknown as GlobalWindow).lintrk?.('track', { conversion_id: 8934260 });
    localStorage.setItem(OAUTH_SIGNUP_FLAG_KEY, 'true');
    try {
      const oAuthResp = await oAuthAzureADAuthenticate(params);
      redirectToOauth(oAuthResp.action);
    } catch (e) {
      localStorage.removeItem(OAUTH_SIGNUP_FLAG_KEY);
      message.error({ content: 'Some error occurred.' });
    }
  }

  async function signInWithGoogle(params: OAuthGoogleAuthenticatePayload) {
    //linkedin track
    (window as unknown as GlobalWindow).lintrk?.('track', { conversion_id: 8934260 });
    // Mark that OAuth was initiated so we can create a HubSpot contact post-login
    // if this turns out to be a new user (checked via isNewUser after page reload)
    localStorage.setItem(OAUTH_SIGNUP_FLAG_KEY, 'true');
    try {
      const oAuthResp = await oAuthGoogleAuthenticate(params);
      redirectToOauth(oAuthResp.action);
    } catch (e) {
      localStorage.removeItem(OAUTH_SIGNUP_FLAG_KEY);
      message.error({ content: 'Some error occurred.' });
    }
  }

  async function signInWithSaml(params: { identifier?: string } = {}) {
    localStorage.setItem(OAUTH_SIGNUP_FLAG_KEY, 'true');
    try {
      const oAuthResp = await oAuthSamlAuthenticate(params);

      const url = appendQueryParams(oAuthResp.action, {
        SAMLRequest: oAuthResp.SAMLRequest,
        RelayState: oAuthResp.RelayState
      });

      redirectToOauth(url);
    } catch (e) {
      localStorage.removeItem(OAUTH_SIGNUP_FLAG_KEY);
      message.error({ content: 'Some error occurred.' });
    }
  }

  return { idpTypesSupported, isLoading, signInWithAzureAD, signInWithSaml, signInWithGoogle };
}
